import { ChangeDetectionStrategy, Component, computed, effect, inject, input, model, output, signal } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { HdChartCreateFormComponent } from './hd-chart-create-form.component';
import { HdCompositeCreateFormComponent } from './hd-composite-create-form.component';
import { HdChartCalculationResult } from './hd-chart-library.models';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';

/** Aanmaak-soort in de dialog: gewoon geboorte-formulier of composiet uit twee library-charts. */
type HdCreateKind = 'standard' | 'composite';

@Component({
  selector: 'hd-chart-create-dialog',
  standalone: true,
  imports: [SharedModule, TranslocoModule, HdChartCreateFormComponent, HdCompositeCreateFormComponent],
  templateUrl: './hd-chart-create-dialog.component.html',
  styleUrl: './hd-chart-create-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdChartCreateDialogComponent {
  private readonly transloco = inject(TranslocoService);
  private readonly contentLang = inject(HdContentLanguageService);

  readonly open = model(false);
  /** false (bv. manager-paginamodus): de host kan geen onopgeslagen chart tonen → vinkje "enkel berekenen" verbergen. */
  readonly calculateOnlyOffered = input(true);
  readonly calculated = output<HdChartCalculationResult>();

  /** Gekozen soort; elke keer dat de dialog opent terug naar standaard. */
  protected readonly kind = signal<HdCreateKind>('standard');

  constructor() {
    effect(() => {
      if (this.open()) {
        this.kind.set('standard');
      }
    });
  }

  protected readonly text = computed(() => {
    this.contentLang.translationsLoadedTick();
    const language = this.contentLang.effectiveLang();
    const translate = (key: string) => this.transloco.translate<string>(key, undefined, language);
    return {
      title: translate('humanDesign.chart.createDialog.title'),
      close: translate('actions.close'),
      kindStandard: translate('humanDesign.chart.createDialog.kindStandard'),
      kindComposite: translate('humanDesign.chart.createDialog.kindComposite'),
    };
  });

  protected setKind(kind: HdCreateKind): void {
    this.kind.set(kind);
  }

  onCalculated(result: HdChartCalculationResult): void {
    this.calculated.emit(result);
    this.open.set(false);
  }
}
